import prefersContrast, {
  contrastPreferences,
} from '@magica11y/prefers-contrast'
import type { ContrastPreference } from '@magica11y/prefers-contrast'

/**
 * Checks if the prefers-contrast media query is supported in the current browser.
 * Note: Browser support for this feature is limited and varies by browser and OS.
 * Safari has the best support, while Chrome and Firefox may not detect macOS system settings.
 *
 * @returns true if the feature is supported, false otherwise
 */
export function isPrefersContrastSupported(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false
  }

  // Check if the media query is supported
  try {
    const mediaQuery = window.matchMedia('(prefers-contrast)')
    // If the media query doesn't throw and has a matches property, it's supported
    return typeof mediaQuery.matches === 'boolean'
  } catch {
    return false
  }
}

/**
 * Gets the user's preferred contrast setting.
 * Uses the @magica11y/prefers-contrast library to detect user preferences.
 *
 * **Browser Support:** Limited browser support. Safari has the best support.
 * Chrome and Firefox may not detect macOS system-level contrast settings.
 *
 * @returns 'high' | 'low' | 'no-preference' | 'custom' | null - The preferred contrast setting, or null if preference cannot be determined
 */
export function getContrastPreference(): ContrastPreference {
  if (typeof window === 'undefined') {
    return null
  }

  const contrastPreference = prefersContrast()
  if (contrastPreference === contrastPreferences.HIGH) {
    return 'high'
  }
  if (contrastPreference === contrastPreferences.LOW) {
    return 'low'
  }
  if (contrastPreference === contrastPreferences.NO_PREFERENCE) {
    return 'no-preference'
  }
  if (contrastPreference === contrastPreferences.CUSTOM) {
    return 'custom'
  }
  return null
}

/**
 * Checks if the user prefers increased contrast (more or high).
 * This detects when users have enabled "Increase Contrast" or similar settings
 * in their system preferences, which corresponds to the CSS `prefers-contrast: more`
 * or the magica11y `HIGH` value.
 *
 * Uses `isPrefersContrastSupported()` to check if the feature is supported,
 * and `getContrastPreference()` to get the user's preference. Also checks
 * the media query directly for "more" value as a fallback.
 *
 * **Browser Support:** Limited browser support. Safari has the best support.
 * Chrome and Firefox may not detect macOS system-level contrast settings.
 *
 * @returns true if the user prefers increased contrast (more or high), false otherwise
 */
export function checkPrefersIncreasedContrast(): boolean {
  if (!isPrefersContrastSupported()) {
    return false
  }

  // Check using getContrastPreference for 'high' value
  const preference = getContrastPreference()
  if (preference === contrastPreferences.HIGH) {
    return true
  }

  // Also check media query directly for "more" value (CSS spec)
  if (window.matchMedia) {
    try {
      const moreContrast = window.matchMedia('(prefers-contrast: more)')
      if (moreContrast.matches) {
        return true
      }
    } catch {
      // Media query not supported, continue
    }
  }

  return false
}
